<template>
    <div class="portfolio-stats">
        <div class="portfolio-stats-header">
            <div class="portfolio-stats-heading">
                <h2 class="portfolio-stats-title">{{ $t('titles_JournalStats.portfolio') }}</h2>
                <p class="portfolio-stats-subtitle">{{ $t('pNl.title') }}</p>
            </div>
            <v-select
                v-model="chartMetric"
                :items="metricOptions"
                item-text="label"
                item-value="value"
                dense
                outlined
                hide-details
                class="portfolio-metric-select"
                @change="setData"
            />
        </div>

        <apexchart
            type="area"
            height="350"
            :options="optionsPNLChart"
            :series="seriesPNLChart"
        />

        <div class="portfolio-stat-cards">
            <div
                v-for="card in statCards"
                :key="card.key"
                class="portfolio-stat-card"
            >
                <h4 class="portfolio-stat-card__title">{{ card.title }}</h4>
                <p class="portfolio-stat-card__meta">{{ card.meta }}</p>
                <p class="portfolio-stat-card__value" :class="card.valueClass">
                    {{ card.value }}
                </p>
            </div>
        </div>

        <JournalInsightBar :insight="insight" />
    </div>
</template>

<script>
import InsightsRepository from '@/repositories/insights';
import JournalInsightBar from '@/views/Journal/components/JournalInsightBar';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function createEmptyAreaChartOptions() {
    return {
        chart: {
            type: 'area',
            toolbar: { show: false },
            zoom: { enabled: false },
        },
        legend: { show: false },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2, colors: ['#005931'] },
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.35,
                opacityTo: 0.05,
                stops: [0, 100],
            },
            colors: ['#005931'],
        },
        grid: {
            borderColor: '#f3f4f6',
            strokeDashArray: 4,
            padding: { left: 12, right: 12 },
        },
        xaxis: {
            type: 'datetime',
            axisBorder: { show: false },
            axisTicks: { show: false },
            labels: {
                show: true,
                style: { colors: '#ababab', fontSize: '12px', fontFamily: 'Avenir, sans-serif' },
            },
        },
        yaxis: {
            tickAmount: 4,
            labels: {
                show: true,
                style: { colors: '#ababab', fontSize: '12px', fontFamily: 'Avenir, sans-serif' },
            },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        tooltip: {
            x: { format: 'dd/MM/yyyy HH:mm' },
        },
        annotations: { yaxis: [] },
    };
}

export default {
    name: 'PortfolioStats',
    components: { JournalInsightBar },
    props: {
        // AI insight for this section, already resolved by JournalStats (null = nothing to show).
        insight: { type: Object, default: null },
    },
    data() {
        return {
            chartMetric: 'balance',
            seriesPNLChart: [{ name: 'Performance', data: [] }],
            optionsPNLChart: createEmptyAreaChartOptions(),
            statCards: [],
            profitTarget: null,
            profitTargetActive: false,
        };
    },
    computed: {
        metricOptions() {
            return [
                { value: 'balance', label: this.$t('portfolioStats.metrics.balance') },
                { value: 'pnl', label: this.$t('portfolioStats.metrics.pnl') },
            ];
        },
    },
    beforeMount() {
        this.loadTradingPlanTarget();
        this.setData();
    },
    watch: {
        '$store.state.book.trades': function () {
            this.setData();
        },
        '$i18n.locale': function () {
            this.setData();
        },
    },
    methods: {
        async loadTradingPlanTarget() {
            try {
                const response = await InsightsRepository.getUserTradingPlan();
                if (response.status >= 400 || !response.data) {
                    return;
                }
                const targetResult = (response.data.tradingPlanInsights || []).find(
                    (restriction) => restriction.insightType === 'PROFIT_RESTRICTION'
                );
                if (targetResult && targetResult.active) {
                    this.profitTargetActive = true;
                    this.profitTarget = Number(targetResult.profitTarget) || null;
                }
            } catch (error) {
                // Trading plan is optional for this section.
            }
        },
        setData() {
            const trades = this.$store.state.book.trades.slice().sort(
                (a, b) => this.getTradeTimestamp(a) - this.getTradeTimestamp(b)
            );

            if (!trades.length) {
                this.seriesPNLChart = [{ name: 'Performance', data: [] }];
                this.statCards = this.buildEmptyStatCards();
                this.optionsPNLChart = this.buildChartOptions([], 0, null);
                this.$emit('aggregates', null);
                return;
            }

            const initialBalance = this.getInitialBalance(trades);
            const timeline = this.buildTimeline(trades, initialBalance);
            const chartData = timeline.map((point) => [point.time, this.chartMetric === 'balance' ? point.balance : point.pnl]);
            const finalPnl = timeline[timeline.length - 1].pnl;

            this.seriesPNLChart = [{
                name: this.chartMetric === 'balance'
                    ? this.$t('portfolioStats.metrics.balance')
                    : this.$t('portfolioStats.metrics.pnl'),
                data: chartData,
            }];

            this.statCards = this.buildStatCards(trades, initialBalance, finalPnl, timeline);
            this.optionsPNLChart = this.buildChartOptions(chartData, initialBalance, finalPnl);
            this.$emit('aggregates', this.buildInsightAggregates(trades, initialBalance, finalPnl, timeline));
        },
        buildInsightAggregates(trades, initialBalance, finalPnl, timeline) {
            const { highestProfitTrade, lowestLostTrade, highestBalancePoint, lowestBalancePoint } =
                this.findExtremes(trades, timeline);
            const round = (value) => Math.round(value * 100) / 100;
            const day = (date) => (date ? new Date(date).toISOString().slice(0, 10) : '');
            const aggregates = {
                trades: trades.length,
                winningTrades: trades.filter((trade) => trade.netProfit > 0).length,
                losingTrades: trades.filter((trade) => trade.netProfit < 0).length,
                initialBalance: round(initialBalance),
                finalBalance: round(initialBalance + finalPnl),
                netProfit: round(finalPnl),
                highestProfit: {
                    amount: round(highestProfitTrade.netProfit),
                    date: day(highestProfitTrade.closeTime || highestProfitTrade.openTime),
                },
                lowestLost: {
                    amount: round(lowestLostTrade.netProfit),
                    date: day(lowestLostTrade.closeTime || lowestLostTrade.openTime),
                },
                highestBalance: {
                    amount: round(highestBalancePoint.balance),
                    date: day(highestBalancePoint.trade.closeTime || highestBalancePoint.trade.openTime),
                },
                lowestBalance: {
                    amount: round(lowestBalancePoint.balance),
                    date: day(lowestBalancePoint.trade.closeTime || lowestBalancePoint.trade.openTime),
                },
            };
            if (this.profitTargetActive && this.profitTarget != null) {
                aggregates.profitTarget = round(this.profitTarget);
                aggregates.pnlVsTarget = round(finalPnl - this.profitTarget);
            }
            return aggregates;
        },
        findExtremes(trades, timeline) {
            let highestProfitTrade = trades[0];
            let lowestLostTrade = trades[0];
            let highestBalancePoint = timeline[0];
            let lowestBalancePoint = timeline[0];

            trades.forEach((trade) => {
                if (trade.netProfit > highestProfitTrade.netProfit) {
                    highestProfitTrade = trade;
                }
                if (trade.netProfit < lowestLostTrade.netProfit) {
                    lowestLostTrade = trade;
                }
            });

            timeline.forEach((point) => {
                if (point.balance > highestBalancePoint.balance) {
                    highestBalancePoint = point;
                }
                if (point.balance < lowestBalancePoint.balance) {
                    lowestBalancePoint = point;
                }
            });

            return { highestProfitTrade, lowestLostTrade, highestBalancePoint, lowestBalancePoint };
        },
        getTradeTimestamp(trade) {
            const date = trade.closeTime || trade.openTime;
            return date ? date.getTime() : 0;
        },
        getInitialBalance(trades) {
            for (let i = 0; i < trades.length; i += 1) {
                const trade = trades[i];
                if (trade.accountBalance != null && trade.netProfit != null) {
                    return trade.accountBalance - trade.netProfit;
                }
            }
            return 0;
        },
        buildTimeline(trades, initialBalance) {
            let cumulativePnl = 0;
            return trades.map((trade) => {
                cumulativePnl += trade.netProfit;
                return {
                    time: this.getTradeTimestamp(trade),
                    trade,
                    pnl: cumulativePnl,
                    balance: initialBalance + cumulativePnl,
                };
            });
        },
        buildChartOptions(chartData, initialBalance, finalPnl) {
            const values = chartData.map((point) => point[1]);
            const maxValue = values.length ? Math.max(...values) : 0;
            const minValue = values.length ? Math.min(...values) : 0;
            const initialLine = this.chartMetric === 'balance' ? initialBalance : 0;
            const targetLine = this.profitTargetActive && this.profitTarget != null
                ? (this.chartMetric === 'balance' ? initialBalance + this.profitTarget : this.profitTarget)
                : null;

            let yMin = Math.min(minValue, initialLine);
            let yMax = Math.max(maxValue, initialLine);
            if (targetLine != null) {
                yMin = Math.min(yMin, targetLine);
                yMax = Math.max(yMax, targetLine);
            }
            const padding = Math.max((yMax - yMin) * 0.1, 10);

            const annotations = [{
                y: initialLine,
                borderColor: '#ababab',
                strokeDashArray: 4,
                label: {
                    text: this.$t('portfolioStats.chartLabels.initial'),
                    borderWidth: 0,
                    style: {
                        background: 'transparent',
                        color: '#ababab',
                        fontSize: '12px',
                        fontFamily: 'Avenir, sans-serif',
                    },
                    position: 'right',
                    offsetX: 0,
                },
            }];

            if (targetLine != null) {
                annotations.push({
                    y: targetLine,
                    borderColor: '#005931',
                    strokeDashArray: 4,
                    label: {
                        text: this.$t('portfolioStats.chartLabels.target'),
                        borderWidth: 0,
                        style: {
                            background: 'transparent',
                            color: '#005931',
                            fontSize: '12px',
                            fontFamily: 'Avenir, sans-serif',
                        },
                        position: 'right',
                        offsetX: 0,
                    },
                });
            }

            return {
                theme: {
                    mode: this.$store.state.currentTheme == 'dark' ? 'dark' : 'light',
                },
                noData: {
                    text: 'No data for the current filter',
                    align: 'center',
                    verticalAlign: 'middle',
                    style: {
                        color: '#111111',
                        fontSize: '21px',
                        fontFamily: 'Ubuntu, sans-serif',
                    },
                },
                chart: {
                    type: 'area',
                    toolbar: { show: false },
                    zoom: { enabled: false },
                },
                legend: { show: false },
                dataLabels: { enabled: false },
                stroke: {
                    curve: 'smooth',
                    width: 2,
                    colors: ['#005931'],
                },
                fill: {
                    type: 'gradient',
                    gradient: {
                        shadeIntensity: 1,
                        opacityFrom: 0.35,
                        opacityTo: 0.05,
                        stops: [0, 100],
                    },
                    colors: ['#005931'],
                },
                grid: {
                    borderColor: '#f3f4f6',
                    strokeDashArray: 4,
                    padding: { left: 12, right: 48 },
                },
                annotations: { yaxis: annotations },
                xaxis: {
                    type: 'datetime',
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                    labels: {
                        style: {
                            colors: '#ababab',
                            fontSize: '12px',
                            fontFamily: 'Avenir, sans-serif',
                        },
                    },
                },
                yaxis: {
                    min: yMin - padding,
                    max: yMax + padding,
                    tickAmount: 4,
                    labels: {
                        formatter: (value) => this.formatAxisCurrency(value),
                        style: {
                            colors: '#ababab',
                            fontSize: '12px',
                            fontFamily: 'Avenir, sans-serif',
                        },
                    },
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                },
                tooltip: {
                    x: { format: 'dd/MM/yyyy HH:mm' },
                    y: {
                        formatter: (value) => this.formatAxisCurrency(value),
                    },
                },
            };
        },
        buildEmptyStatCards() {
            const placeholders = [
                { key: 'highestProfit', titleKey: 'highestProfit', valueClass: 'portfolio-stat-card__value--positive' },
                { key: 'highestBalance', titleKey: 'highestBalance', valueClass: 'portfolio-stat-card__value--positive' },
                { key: 'lowestLost', titleKey: 'lowestLost', valueClass: 'portfolio-stat-card__value--negative' },
                { key: 'lowestBalance', titleKey: 'lowestBalance', valueClass: 'portfolio-stat-card__value--negative' },
                { key: 'aboveTarget', titleKey: 'aboveTarget', valueClass: 'portfolio-stat-card__value--positive' },
            ];

            return placeholders.map((item) => ({
                key: item.key,
                title: this.$t(`portfolioStats.cards.${item.titleKey}`),
                meta: item.key === 'aboveTarget'
                    ? this.$t('portfolioStats.cards.noTargetOnPlan')
                    : '—',
                value: '—',
                valueClass: 'portfolio-stat-card__value--neutral',
            }));
        },
        buildStatCards(trades, initialBalance, finalPnl, timeline) {
            if (!trades.length) {
                return this.buildEmptyStatCards();
            }

            const { highestProfitTrade, lowestLostTrade, highestBalancePoint, lowestBalancePoint } =
                this.findExtremes(trades, timeline);

            const hasTarget = this.profitTargetActive && this.profitTarget != null;
            const aboveTargetValue = hasTarget ? finalPnl - this.profitTarget : finalPnl;

            return [
                {
                    key: 'highestProfit',
                    title: this.$t('portfolioStats.cards.highestProfit'),
                    meta: this.formatStatDate(highestProfitTrade.closeTime || highestProfitTrade.openTime),
                    value: this.formatSignedValue(highestProfitTrade.netProfit),
                    valueClass: 'portfolio-stat-card__value--positive',
                },
                {
                    key: 'highestBalance',
                    title: this.$t('portfolioStats.cards.highestBalance'),
                    meta: this.formatStatDate(highestBalancePoint.trade.closeTime || highestBalancePoint.trade.openTime),
                    value: this.formatPlainValue(highestBalancePoint.balance),
                    valueClass: 'portfolio-stat-card__value--positive',
                },
                {
                    key: 'lowestLost',
                    title: this.$t('portfolioStats.cards.lowestLost'),
                    meta: this.formatStatDate(lowestLostTrade.closeTime || lowestLostTrade.openTime),
                    value: this.formatSignedValue(lowestLostTrade.netProfit),
                    valueClass: 'portfolio-stat-card__value--negative',
                },
                {
                    key: 'lowestBalance',
                    title: this.$t('portfolioStats.cards.lowestBalance'),
                    meta: this.formatStatDate(lowestBalancePoint.trade.closeTime || lowestBalancePoint.trade.openTime),
                    value: this.formatPlainValue(lowestBalancePoint.balance),
                    valueClass: 'portfolio-stat-card__value--negative',
                },
                {
                    key: 'aboveTarget',
                    title: this.$t('portfolioStats.cards.aboveTarget'),
                    meta: hasTarget
                        ? this.$t('portfolioStats.cards.targetOnPlan', {
                            amount: this.formatPlainValue(this.profitTarget),
                        })
                        : this.$t('portfolioStats.cards.noTargetOnPlan'),
                    value: this.formatSignedValue(aboveTargetValue),
                    valueClass: aboveTargetValue >= 0
                        ? 'portfolio-stat-card__value--positive'
                        : 'portfolio-stat-card__value--negative',
                },
            ];
        },
        formatStatDate(date) {
            if (!date) {
                return '';
            }
            const value = new Date(date);
            const month = MONTHS[value.getMonth()];
            const day = String(value.getDate()).padStart(2, '0');
            const year = value.getFullYear();
            let hours = value.getHours();
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12 || 12;
            const minutes = String(value.getMinutes()).padStart(2, '0');
            return `${month}/${day}/${year} - ${hours}:${minutes} ${ampm}`;
        },
        formatPlainValue(value) {
            const abs = Math.abs(value);
            if (abs >= 1000) {
                return `$${(abs / 1000).toFixed(2)}K`;
            }
            return `$${abs.toFixed(1)}`;
        },
        formatSignedValue(value) {
            const abs = Math.abs(value);
            const formatted = abs >= 1000
                ? `$${(abs / 1000).toFixed(2)}K`
                : `$${abs.toFixed(1)}`;
            if (value > 0) {
                return `+${formatted}`;
            }
            if (value < 0) {
                return `-${formatted}`;
            }
            return formatted;
        },
        formatAxisCurrency(value) {
            const abs = Math.abs(value);
            const formatted = abs >= 1000
                ? `${(abs / 1000).toFixed(1)}k`
                : abs.toFixed(0);
            return value >= 0 ? `$${formatted}` : `-$${formatted}`;
        },
    },
};
</script>

<style lang="scss" scoped>
.portfolio-stats {
    .portfolio-stats-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 20px;
        flex-wrap: wrap;
    }

    .portfolio-stats-title {
        margin: 0 0 4px;
        font-family: 'Avenir', sans-serif;
        font-weight: 700;
        font-size: 28px;
        color: var(--v-darkGrey-base);
    }

    .portfolio-stats-subtitle {
        margin: 0;
        font-family: 'Avenir', sans-serif;
        font-size: 14px;
        line-height: 1.4;
        color: #ababab !important;
    }

    .portfolio-metric-select {
        max-width: 180px;
        flex-shrink: 0;
        margin-top: 4px;

        ::v-deep .v-input__control {
            min-height: 36px !important;
        }

        ::v-deep .v-input__slot {
            min-height: 36px !important;
            border-color: #ababab !important;
        }

        ::v-deep fieldset {
            border-color: #ababab !important;
        }
    }

    .portfolio-stat-cards {
        display: grid;
        grid-template-columns: repeat(5, minmax(0, 1fr));
        gap: 8px;
        align-items: start;
    }

    .portfolio-stat-card {
        min-width: 0;
        text-align: left;
        padding-right: 8px;

        &__title {
            margin: 0 0 2px;
            font-family: 'Avenir', sans-serif;
            font-weight: 700;
            font-size: 16px;
            line-height: 1.25;
            color: #333 !important;
        }

        &__meta {
            margin: 0 0 6px;
            font-family: 'Avenir', sans-serif;
            font-size: 13px;
            line-height: 1.3;
            color: #ababab !important;
            min-height: 0;
        }

        &__value {
            margin: 0;
            font-family: 'Avenir', sans-serif;
            font-weight: 700;
            font-size: 24px;
            line-height: 1.2;

            &--positive {
                color: #005931 !important;
            }

            &--negative {
                color: #d00808 !important;
            }

            &--neutral {
                color: #ababab !important;
            }
        }
    }

    @media (max-width: 1263px) {
        .portfolio-stat-cards {
            grid-template-columns: repeat(3, minmax(0, 1fr));
        }
    }

    @media (max-width: 959px) {
        .portfolio-stat-cards {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }
    }

    @media (max-width: 599px) {
        .portfolio-stat-cards {
            grid-template-columns: 1fr;
        }
    }
}
</style>

<style lang="scss">
.app {
    .portfolio-stats {
        .apexcharts-text {
            fill: var(--v-white-base);
        }
    }
}
</style>
